import React, { useEffect, useMemo, useState } from "react";
import Select, { components } from "react-select";
import { ChevronDownIcon } from "../../../helper/HwIcon";
import { adminApi } from "../api/adminApi";

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}><ChevronDownIcon /></components.DropdownIndicator>
);
const rsComponents = { DropdownIndicator, IndicatorSeparator: () => null };
const locSelectStyles = {
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  menu: (b) => ({ ...b, zIndex: 9999, fontSize: 13 }),
  menuList: (b) => ({ ...b, paddingTop: 4, paddingBottom: 4 }),
  control: (b, s) => ({
    ...b, minHeight: 42, width: "100%",
    borderColor: s.isFocused ? "#3264FF" : "#e2e6ee",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(50,100,255,.12)" : "none",
    "&:hover": { borderColor: "#c3ccdb" }, fontSize: 14,
    backgroundColor: s.isDisabled ? "#f4f6fa" : "#fff",
  }),
  option: (b, s) => ({
    ...b, fontSize: 13.5, padding: "8px 12px",
    backgroundColor: s.isSelected ? "#3264FF" : s.isFocused ? "#eef2ff" : "#fff",
    color: s.isSelected ? "#fff" : "#1b2439",
  }),
  singleValue: (b) => ({ ...b, fontSize: 14 }),
};

const VAT_OPTS = [
  { key: "zero", label: "Zero VAT", vatRateType: "zero-vat", exclusiveOrInclusive: "" },
  { key: "excl", label: "Standard VAT (Exclusive)", vatRateType: "standard-vat", exclusiveOrInclusive: "exclusive-rate" },
  { key: "incl", label: "Standard VAT (Inclusive)", vatRateType: "standard-vat", exclusiveOrInclusive: "inclusive-rate" },
];

const VENDOR_OPTS = [
  { value: "rayna", label: "Rayna" },
  { value: "parmar", label: "Parmar" },
];

const vatKey = (c) => {
  if (String(c?.vatRateType || "").toLowerCase() === "standard-vat") {
    return String(c?.exclusiveOrInclusive || "").toLowerCase() === "inclusive-rate" ? "incl" : "excl";
  }
  return "zero";
};

const WEEK_DAYS = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

const toYmd = (v) => {
  const s = String(v || "").trim();
  const m = s.match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

const normDays = (arr) =>
  (Array.isArray(arr) ? arr : []).map((d) => String(d).toLowerCase());

const VatManage = () => {
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [cityId, setCityId] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("rayna");
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);
  const [blkRow, setBlkRow] = useState(null);
  const [blkDates, setBlkDates] = useState([]);
  const [blkDays, setBlkDays] = useState([]);
  const [blkDateInput, setBlkDateInput] = useState("");
  const [blkSaving, setBlkSaving] = useState(false);

  const defaultCountryId = (list) => {
    const uae = list.find((c) => /united arab emirates|uae/i.test(String(c.countryName || "")));
    if (uae) return String(uae.countryId);
    return list[0] ? String(list[0].countryId) : "";
  };

  const load = async () => {
    setLoading(true);
    try {
      const [catRes, locRes] = await Promise.all([
        adminApi("GET", "/admin/excursions/catalog"),
        adminApi("GET", "/admin/excursions/locations"),
      ]);
      const items = (catRes?.data?.data?.excursions || []).filter((c) => ["rayna", "parmar"].includes(String(c.source || "").toLowerCase()));
      const list = locRes?.data?.data?.countries || [];
      setRows(items);
      setCountries(list);
      setCountryId((cur) => cur || defaultCountryId(list));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const selectedCountry = countries.find((c) => String(c.countryId) === String(countryId));
  const cityOptions = selectedCountry?.cities || [];

  const cityNameById = useMemo(() => {
    const m = new Map();
    countries.forEach((co) => (co.cities || []).forEach((ci) => m.set(String(ci.cityId), ci.cityName)));
    return m;
  }, [countries]);

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return rows
      .filter((c) => String(c.source || "").toLowerCase() === source)
      .filter((c) => !countryId || String(c.countryId) === String(countryId))
      .filter((c) => !cityId || String(c.cityId) === String(cityId))
      .filter((c) => !n || String(c.tourName || "").toLowerCase().includes(n))
      .sort((a, b) => String(a.tourName || "").localeCompare(String(b.tourName || "")));
  }, [rows, countryId, cityId, q, source]);

  const vatCount = useMemo(() => rows.filter((c) => String(c.source || "").toLowerCase() === source && vatKey(c) !== "zero").length, [rows, source]);

  const openBlackout = (row) => {
    setBlkRow(row);
    setBlkDates([...new Set((Array.isArray(row.blackoutDates) ? row.blackoutDates : []).map(toYmd).filter(Boolean))].sort());
    setBlkDays(normDays(row.blackoutDays));
    setBlkDateInput("");
  };

  const closeBlackout = () => {
    setBlkRow(null);
    setBlkDates([]);
    setBlkDays([]);
    setBlkDateInput("");
  };

  const addBlkDate = () => {
    const d = toYmd(blkDateInput);
    if (!d) return;
    setBlkDates((ds) => (ds.includes(d) ? ds : [...ds, d].sort()));
    setBlkDateInput("");
  };

  const removeBlkDate = (d) => setBlkDates((ds) => ds.filter((x) => x !== d));

  const toggleBlkDay = (key) =>
    setBlkDays((ds) => (ds.includes(key) ? ds.filter((x) => x !== key) : [...ds, key]));

  const saveBlackout = async () => {
    if (!blkRow) return;
    const dates = [...new Set(blkDates.map(toYmd).filter(Boolean))];
    const days = WEEK_DAYS.map((d) => d.key).filter((k) => blkDays.includes(k));
    setBlkSaving(true);
    try {
      await adminApi("POST", "/admin/excursions/blackout", { source: blkRow.source || source, tourId: blkRow.tourId, blackoutDates: dates, blackoutDays: days });
      setRows((rs) => rs.map((c) => (c.tourId === blkRow.tourId ? { ...c, blackoutDates: dates, blackoutDays: days } : c)));
      setToast({ type: "ok", text: "Blackout updated" });
      closeBlackout();
    } catch {
      setToast({ type: "err", text: "Could not update blackout" });
    } finally {
      setBlkSaving(false);
    }
  };

  const setVat = async (row, key) => {
    const opt = VAT_OPTS.find((o) => o.key === key) || VAT_OPTS[0];
    const prev = { vatRateType: row.vatRateType, exclusiveOrInclusive: row.exclusiveOrInclusive };
    setBusy(row.tourId);
    setRows((rs) => rs.map((c) => (c.tourId === row.tourId ? { ...c, vatRateType: opt.vatRateType, exclusiveOrInclusive: opt.exclusiveOrInclusive } : c)));
    try {
      await adminApi("POST", "/admin/excursions/vat", { source: row.source || source, tourId: row.tourId, vatRateType: opt.vatRateType, exclusiveOrInclusive: opt.exclusiveOrInclusive });
      setToast({ type: "ok", text: `VAT updated for ${row.tourName || "product"}` });
    } catch {
      setRows((rs) => rs.map((c) => (c.tourId === row.tourId ? { ...c, ...prev } : c)));
      setToast({ type: "err", text: "Could not update VAT" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="gp">
      {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}

      <div className="gp-head">
        <div>
          <h1 className="gp-title">Manage Tour VAT ({source === "parmar" ? "Parmar" : "Rayna"})</h1>
          <p className="gp-sub">Set VAT per {source === "parmar" ? "Parmar" : "Rayna"} product. Default is Zero VAT; changes save to the catalog. {vatCount} product(s) have VAT.</p>
        </div>
      </div>

      <div className="gp-card">
        <div className="gp-loc" style={{ marginBottom: 18 }}>
          <div className="fld">
            <label>Vendor</label>
            <Select
              options={VENDOR_OPTS}
              value={VENDOR_OPTS.find((o) => o.value === source) || VENDOR_OPTS[0]}
              onChange={(opt) => setSource(opt?.value || "rayna")}
              components={rsComponents}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={locSelectStyles}
            />
          </div>
          <div className="fld">
            <label>Country</label>
            <Select
              options={countries.map((c) => ({ value: String(c.countryId), label: c.countryName }))}
              value={countryId ? { value: String(countryId), label: selectedCountry?.countryName || "" } : null}
              onChange={(opt) => { setCountryId(opt?.value || ""); setCityId(""); }}
              placeholder="Select country"
              isClearable
              components={rsComponents}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={locSelectStyles}
            />
          </div>
          <div className="fld">
            <label>City</label>
            <Select
              options={cityOptions.map((c) => ({ value: String(c.cityId), label: c.cityName }))}
              value={cityId ? { value: String(cityId), label: cityOptions.find((c) => String(c.cityId) === String(cityId))?.cityName || "" } : null}
              onChange={(opt) => setCityId(opt?.value || "")}
              isDisabled={!countryId}
              placeholder="All cities"
              isClearable
              components={rsComponents}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={locSelectStyles}
            />
          </div>
          <div className="fld" style={{ flex: 1, maxWidth: 400 }}>
            <label>Search</label>
            <input className="inp" placeholder="Search product…" value={q} onChange={(e) => setQ(e.target.value)} style={{ height: 42, fontFamily: "inherit", fontSize: 14, boxSizing: "border-box" }} />
          </div>
        </div>

        <div className="gp-tablewrap">
          <table className="gp-t">
            <thead>
              <tr>
                <th>Tour</th>
                <th style={{ width: 260 }}>VAT</th>
                <th style={{ width: 200 }}>Blackout</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: 24, color: "#98a2b6" }}>Loading…</td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: 24, color: "#98a2b6" }}>No {source === "parmar" ? "Parmar" : "Rayna"} products.</td></tr>
              ) : (
                shown.map((c) => (
                  <tr key={`${c.source}:${c.tourId}`} className={vatKey(c) !== "zero" ? "row-sel" : ""}>
                    <td style={{ fontWeight: 600 }}>
                      {c.tourName || "—"}
                      {cityNameById.get(String(c.cityId)) ? (
                        <span style={{ display: "block", fontWeight: 400, fontSize: 12, color: "#98a2b6", marginTop: 2 }}>
                          {cityNameById.get(String(c.cityId))}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <select
                        className="inp"
                        style={{ width: "100%", height: 40, fontFamily: "inherit", fontSize: 14, background: "#fff", cursor: "pointer", boxSizing: "border-box" }}
                        value={vatKey(c)}
                        disabled={busy === c.tourId}
                        onChange={(e) => setVat(c, e.target.value)}
                      >
                        {VAT_OPTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                    </td>
                    <td>
                      {(() => {
                        const nd = Array.isArray(c.blackoutDates) ? c.blackoutDates.length : 0;
                        const nw = Array.isArray(c.blackoutDays) ? c.blackoutDays.length : 0;
                        const total = nd + nw;
                        return (
                          <button
                            type="button"
                            className="btn-ghost"
                            style={{ height: 40, padding: "0 14px", fontFamily: "inherit", fontSize: 14, border: "1px solid #e2e6ee", borderRadius: 8, background: total ? "#eef2ff" : "#fff", color: total ? "#3264FF" : "#1b2439", cursor: "pointer", whiteSpace: "nowrap" }}
                            onClick={() => openBlackout(c)}
                          >
                            {total ? `Blackout (${nd}d · ${nw}w)` : "Set blackout"}
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {blkRow ? (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15,22,40,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, padding: 16 }}
          onClick={closeBlackout}
        >
          <div
            style={{ width: "100%", maxWidth: 560, background: "#fff", borderRadius: 14, boxShadow: "0 24px 60px rgba(15,22,40,.28)", maxHeight: "88vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #eef1f6" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1b2439" }}>Blackout — {blkRow.tourName || "—"}</div>
              {cityNameById.get(String(blkRow.cityId)) ? (
                <div style={{ fontSize: 12, color: "#98a2b6", marginTop: 2 }}>{cityNameById.get(String(blkRow.cityId))}</div>
              ) : null}
            </div>

            <div style={{ padding: 22 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2439", marginBottom: 8 }}>Blackout days</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 22 }}>
                {WEEK_DAYS.map((d) => {
                  const on = blkDays.includes(d.key);
                  return (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => toggleBlkDay(d.key)}
                      style={{ height: 38, minWidth: 56, padding: "0 12px", borderRadius: 8, border: on ? "1px solid #3264FF" : "1px solid #e2e6ee", background: on ? "#3264FF" : "#fff", color: on ? "#fff" : "#1b2439", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2439", marginBottom: 8 }}>Blackout dates</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input
                  type="date"
                  className="inp"
                  value={blkDateInput}
                  onChange={(e) => setBlkDateInput(e.target.value)}
                  onClick={(e) => { try { e.currentTarget.showPicker(); } catch (err) {} }}
                  onFocus={(e) => { try { e.currentTarget.showPicker(); } catch (err) {} }}
                  style={{ height: 40, flex: 1, fontFamily: "inherit", fontSize: 14, boxSizing: "border-box", cursor: "pointer" }}
                />
                <button
                  type="button"
                  onClick={addBlkDate}
                  disabled={!toYmd(blkDateInput)}
                  style={{ height: 40, padding: "0 16px", borderRadius: 8, border: "none", background: toYmd(blkDateInput) ? "#3264FF" : "#c3ccdb", color: "#fff", fontFamily: "inherit", fontSize: 14, fontWeight: 600, cursor: toYmd(blkDateInput) ? "pointer" : "not-allowed" }}
                >
                  Add
                </button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, minHeight: 20 }}>
                {blkDates.length === 0 ? (
                  <span style={{ fontSize: 13, color: "#98a2b6" }}>No blackout dates.</span>
                ) : (
                  blkDates.map((d) => (
                    <span key={d} style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 6px 0 12px", borderRadius: 999, background: "#eef2ff", color: "#3264FF", fontSize: 13, fontWeight: 600 }}>
                      {d}
                      <button
                        type="button"
                        onClick={() => removeBlkDate(d)}
                        style={{ width: 20, height: 20, borderRadius: "50%", border: "none", background: "#3264FF", color: "#fff", cursor: "pointer", fontSize: 13, lineHeight: "20px", padding: 0 }}
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>

            <div style={{ padding: "16px 22px", borderTop: "1px solid #eef1f6", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={closeBlackout}
                disabled={blkSaving}
                style={{ height: 42, padding: "0 20px", borderRadius: 8, border: "1px solid #e2e6ee", background: "#fff", color: "#1b2439", fontFamily: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveBlackout}
                disabled={blkSaving}
                style={{ height: 42, padding: "0 22px", borderRadius: 8, border: "none", background: "#3264FF", color: "#fff", fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: blkSaving ? "not-allowed" : "pointer", opacity: blkSaving ? 0.7 : 1 }}
              >
                {blkSaving ? "Saving…" : "Save blackout"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default VatManage;
